import type { FC, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';

import useSwitchAccount from '@zen/Auth/hooks/useSwitchAccount';
import { useTrackAccountSwitch } from '@zen/Components/VisitedAccounts';
import type { Option } from '@zen/DesignSystem/types';
import { opsDashboardRoutes } from '@zen/Routes';
import useAccount from '@zen/utils/hooks/useAccount';
import { useNavigationHistory } from '@zen/utils/NavigationHistory';
import { extractFirstLevelPath } from '@zen/utils/routing';
import type { Nullable, Undefinable } from '@zen/utils/typescript';

import { Form } from '../Form';
import FormAccountSelect from '../Form/FormAccountSelect';
import { validationSchema } from './accountSwitcherForm.validation';
import { OrganizationalRoleEnum } from './types';

interface Props {
  onAccountChange?: () => void;
  submitOnChange?: boolean;
}

const AccountSwitcherForm: FC<Props> = (props) => {
  const { onAccountChange, submitOnChange = false } = props;
  const { accountUuid } = useAccount();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { navigateBack } = useNavigationHistory();
  const { switchToAccount } = useSwitchAccount();
  const trackAccountSwitch = useTrackAccountSwitch();

  const backUrl: string = opsDashboardRoutes.dashboardIndex.getUrl();

  const handleSubmit = (values: { accountId: string }) => {
    const { accountId } = values;

    switchToAccount(accountId);

    return Promise.resolve({ ok: true, error: null });
  };

  const handleSuccess = () => {
    navigateBack(backUrl, (entry) => ({ ...entry, pathname: extractFirstLevelPath(entry.pathname) }));
  };

  const handleOnChange = (newAccountId: Nullable<string>, options: Option<string>[]) => {
    onAccountChange?.();

    if (!submitOnChange || newAccountId === accountUuid) {
      return;
    }

    const companyName: string = options.find(({ value }: Option<string>) => value === newAccountId)?.label || '';

    if (companyName) {
      trackAccountSwitch(companyName, 'accountSwitcherForm');
    }
    if (newAccountId) {
      switchToAccount(newAccountId);
    }
    navigate(extractFirstLevelPath(pathname));
  };

  const initialValues = {
    accountId: accountUuid
  };

  const formButtons: Undefinable<() => ReactNode> = submitOnChange ? (): ReactNode => null : undefined;

  return (
    <Form
      enableReinitialize={true}
      formButtons={formButtons}
      formName="AccountSwitcherForm"
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      validationSchema={validationSchema}
    >
      {() => (
        <FormAccountSelect
          label="Account"
          name="accountId"
          onChange={handleOnChange}
          organizationalRole={[OrganizationalRoleEnum.CONSIGNMENT_PARTY]}
        />
      )}
    </Form>
  );
};

export default AccountSwitcherForm;
